import { Button, Link, Text, Tooltip } from '@shopify/polaris';
import helpers from 'helpers/index';
import { TypedCustomer } from 'interface/customer.model';
import __ from 'languages/index';
import { useCallback, useEffect, useRef, useState } from 'react';
import { MenuHorizontalIcon } from '@shopify/polaris-icons';
import 'media/css/buttomBar.scss';
import { useLocation } from 'react-router-dom';

export default function BottomBar() {
  const DURATION = 90; // giây

  const [countdown, setCountdown] = useState(0);
  const [expiresAt, setExpiresAt] = useState<number | null>(null);
  const timer = useRef<ReturnType<typeof setInterval> | null>(null);
  const previousTicketRef = useRef<string | null>(null);

  const location = useLocation();

  // Rời /ticket_managers thì clear
  useEffect(() => {
    /** Lỗi ở đây, khi load chậm, nó nhận customer, nơi click  */
    const onTicketManagers = location.pathname.startsWith('/ticket_managers') || location.pathname.startsWith('/customer/view/'); // khớp cả /customer/view/23
    if (!onTicketManagers) {
      sessionStorage.removeItem('quickbook_customer_data');
      clearQuickBook(); // bảo đảm dọn cả timer, state
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  const [showQuickBook, setShowQuickBook] = useState(false);
  const [currentCustomerData, setCurrentCustomerData] = useState<TypedCustomer>(null);

  const clearQuickBook = useCallback(() => {
    sessionStorage.removeItem('quickbook_customer_data');
    previousTicketRef.current = null;
    clearTimer();
    setExpiresAt(null);
    setCountdown(0);
    setShowQuickBook(false);
    setCurrentCustomerData(null);
  }, []);

  // Poll sessionStorage + query để kích hoạt QuickBook & reset đồng hồ khi dữ liệu đổi
  useEffect(() => {
    const interval = setInterval(() => {
      const quickbook_customer_data = sessionStorage.getItem('quickbook_customer_data');
      const searchObject = new URLSearchParams(window.location.search);
      const quickbook_journey_id = searchObject.get('journey_id');
      if (helpers.isJson(quickbook_customer_data)) {
        const dataStr = String(quickbook_customer_data);
        setCurrentCustomerData(JSON.parse(dataStr));

        // Nếu dữ liệu vừa thay đổi -> reset expiresAt
        if (previousTicketRef.current !== dataStr) {
          previousTicketRef.current = dataStr;
          setExpiresAt(Date.now() + DURATION * 1000);
        }

        // Chỉ dùng journey_id để QUYẾT ĐỊNH HIỂN THỊ hay không
        if (quickbook_journey_id) {
          setShowQuickBook(true);
        } else {
          // Không có journey_id => ẩn bar, nhưng KHÔNG xóa sessionStorage
          setShowQuickBook(false);
        }
      } else {
        // Thực sự không có dữ liệu hoặc dữ liệu lỗi JSON thì mới clear
        clearQuickBook();
      }
    }, 1000); // 1s là đủ nhạy

    return () => clearInterval(interval);
  }, [clearQuickBook]);

  const clearTimer = () => {
    if (timer.current) {
      clearInterval(timer.current);
      timer.current = null;
    }
  };

  // Đếm ngược theo expiresAt
  useEffect(() => {
    clearTimer();
    if (expiresAt == null) return;

    // set ngay lần đầu
    const firstRemaining = Math.max(0, Math.ceil((expiresAt - Date.now()) / 1000));
    setCountdown(firstRemaining);

    timer.current = setInterval(() => {
      const remaining = Math.ceil((expiresAt - Date.now()) / 1000);
      if (remaining <= 0) {
        setCountdown(0);
        clearQuickBook();
      } else {
        setCountdown(remaining);
      }
    }, 1000);

    return () => clearTimer();
  }, [expiresAt, clearQuickBook]);

  // ESC để hủy nhanh
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') clearQuickBook();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [clearQuickBook]);

  // % progress theo DURATION
  const progressPct = Math.max(0, Math.min(100, (countdown / DURATION) * 100));

  return (
    <div className={`bottom-bar ${showQuickBook ? '' : 'hidden'}`}>
      <div
        className="bottom-bar-progress"
        style={{
          width: `${progressPct}%`,
          transition: 'width 1s linear',
        }}
      ></div>

      <div className="bottom-bar-body">
        <div className="bottom-bar-wrap">
          <div className="left-section hide-on-mobile">
            <div>Đang đặt vé cho</div>
          </div>

          <div className="section">
            <div className="customer_name">
              <Button icon={MenuHorizontalIcon} variant="monochromePlain" url={`/customer/view/${currentCustomerData?.user_id}`}>
                {currentCustomerData?.display_name}
              </Button>
            </div>
            <Text as="h3" variant="headingLg">
              {currentCustomerData?.user_phonenumber}
            </Text>
          </div>

          <div className="section hide-on-mobile">
            <div>Số chỗ</div>
            <div className="big_guy">{helpers.getPad(1)}</div>
          </div>

          <div className="section right-section">
            <Tooltip content="Bấm để hủy ngay hoặc nút ESC">
              <Link removeUnderline onClick={clearQuickBook}>
                Hủy <span>{countdown > 0 && <strong>{countdown}</strong>}</span>
              </Link>
            </Tooltip>
          </div>
        </div>
      </div>
    </div>
  );
}

